import { ChangeEventHandler, startTransition, useLayoutEffect, useRef, useState } from "react";
import { useAsyncState } from "~/client/hooks";
import { useInput, getNameFromLabel } from "../Input";
import { PasswordInputProps, PasswordInputDependencies } from "./PasswordInput.d";
import { mergeRefs, toggleBool } from "~/shared/utils";

/**
 * @function usePasswordInput
 * @param {PasswordInputDependencies} props
 * @returns {PasswordInputProps}
 */
export const usePasswordInput = ({
  initialValue = "",
  validate,
  serverError,
  isRequired = false,
  label,
  syncToSource,
  readOnly = false,
  className,
  onBlur: onBlurDep,
  onChange,
  "data-testid": dataId,
  autoFocus = false,
  ...props
}: PasswordInputDependencies = {}): PasswordInputProps => {
  const [value, setValue] = useAsyncState(initialValue, syncToSource);
  const [show, setShow] = useState(false);
  const [selection, setSelection] = useState<number | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const {
    error,
    onBlur,
    onFocus
  } = useInput({
    "data-testid": dataId,
    value,
    validate,
    serverError,
    onBlur: onBlurDep
  });
  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    setSelection(e.target.selectionStart);
    startTransition(() => {
      onChange?.(e);
      setValue(e.target.value);
    });
  };
  const handleToggle = () => setShow(toggleBool);
  useLayoutEffect(() => {
    ref.current?.setSelectionRange(selection, selection);
  }, [selection]);
  return {
    ...props,
    ref: mergeRefs(props.ref, ref),
    "data-testid": dataId,
    onBlur,
    onFocus,
    error,
    type: show ? "text" : "password",
    value,
    onChange: handleChange,
    name: getNameFromLabel(label),
    required: isRequired,
    readOnly,
    autoFocus,
    className,
    show,
    onToggle: handleToggle
  };
};